<template>
  <div class="entries-table-container" :class="outerContainerClasses">
    <div :class="innerContainerClasses">
      <SfTable
        v-if="props.list.length"
        :auto-header-cell-width="true"
        :handles-sorting="false"
        :headers="headers"
        :initial-sort-by="sortBy"
        :initial-sort-direction="'desc'"
        :rows="sfTableRows"
        :non-sortable-headers="nonSortableHeaders"
        data-testid="entries-table"
        @table-sort="handleTableSort"
      >
        <template #header="{ header, headerIndex, maxHeaderCellWidth }">
          <SfTableHeaderCell
            :align="
              header === 'journal' || header === 'value' ? 'right' : undefined
            "
            :header-id="header.id || header"
            :header-index="headerIndex"
            :max-header-cell-width="maxHeaderCellWidth"
            :sortable="header !== 'document_reference' && header !== 'journal'"
            :sort-direction="sortDirection"
            :sort-by="sortBy"
          >
            <span
              v-if="header !== 'document_reference'"
              v-tooltip="props.translatedHeaders[headerIndex]"
            >
              {{ props.translatedHeaders[headerIndex] }}
            </span>
          </SfTableHeaderCell>
        </template>
        <template #rows="{ rows }">
          <EntriesTableRow
            v-for="row in rows"
            :key="row.id"
            :entry="row"
            :headers="headers"
            :hovered-attributes="hoveredAttributes"
            @enter-row="handleRowHover"
            @leave-row="handleRowLeave"
          />
        </template>
      </SfTable>
      <caption
        v-else
        class="empty sf-block sf-alignc sf-font-l sf-text sf-c-text"
      >
        {{
          I18n.t('common.empty')
        }}
      </caption>
    </div>
    <div v-if="truncated" class="sf-row sf-row-center">{{ truncated }}</div>
  </div>
</template>

<script setup lang="ts">
import type { TableEntry } from '@app/types'

import isNumber from 'lodash/isNumber'
import { computed, ref } from 'vue'

import EntriesTableRow from './entries-table-row.vue'
import SfTable from '@app/shared/vue/components/sf-table/index.vue'
import SfTableHeaderCell from '@app/shared/vue/components/sf-table/sf-table-header-cell.vue'

interface Props {
  attributes?: string[]
  hasMinWidth?: boolean
  headers?: string[]
  isHoverable?: boolean
  list?: TableEntry[]
  translatedHeaders?: string[]
  truncated?: string
}

const props = withDefaults(defineProps<Props>(), {
  attributes: () => [],
  headers: () => [],
  list: () => [],
  translatedHeaders: () => [],
  truncated: '',
})

const I18n = window.I18n

const nonSortableHeaders = ['document_reference', 'journal']

const hoveredAttributes = ref<string[]>([])

const sortBy = ref(
  props.headers[0] === 'journal' ? props.headers[1] : props.headers[0]
)
const sortDirection = ref<'asc' | 'desc'>('desc')

const outerContainerClasses = computed(() => ({
  'entries-table-container--min-width': !!props.hasMinWidth,
}))

const innerContainerClasses = computed(() => ({
  'sf-below-1': !!props.truncated,
}))

const effectiveRows = computed(() => {
  return props.list.map((entry) => ({
    ...entry,

    attributes: props.attributes.reduce((acc, attribute) => {
      if (entry[attribute]) {
        acc[attribute] = entry[attribute]
      }

      return acc
    }, {} as Record<string, unknown>),
  }))
})

const sortedList = computed(() => {
  const listCopy = [...effectiveRows.value]
  return listCopy.sort(sortFunction)
})

const sfTableRows = computed(() => {
  return sortedList.value.map((entry) => {
    const row = props.headers.reduce((acc, header) => {
      acc[header] = (entry as Record<string, unknown>)[header]

      return acc
    }, {} as TableEntry)

    if (props.isHoverable) {
      row.attributes = entry.attributes
    }

    row.journal_link = entry.journal_link
    row.link_to_adjustment = entry.link_to_adjustment

    return row
  })
})

function sortFunction(a: TableEntry, b: TableEntry): number {
  const targetA =
    sortBy.value === 'value' && a['value_raw']
      ? a['value_raw']
      : a[sortBy.value]
  const targetB =
    sortBy.value === 'value' && b['value_raw']
      ? b['value_raw']
      : b[sortBy.value]

  if (isNumber(targetA) && isNumber(targetB)) {
    const numericA = targetA as number
    const numericB = targetB as number

    if (numericA < numericB) {
      return -1 * (sortDirection.value === 'desc' ? -1 : 1)
    }

    if (numericA > numericB) {
      return 1 * (sortDirection.value === 'desc' ? -1 : 1)
    }

    return 0
  } else {
    const sortingHelper = new window.Silverfin.NaturalSorting()

    const result =
      sortingHelper
        .naturalValue(targetA)
        .localeCompare(sortingHelper.naturalValue(targetB)) *
      (sortDirection.value === 'desc' ? -1 : 1)

    return result
  }
}

function handleTableSort({
  sortBy: tsortBy,
  sortDirection: tsortDirection,
}: {
  sortBy: string
  sortDirection: 'asc' | 'desc'
}) {
  if (tsortBy === 'document_reference' || tsortBy === 'journal') {
    return
  }

  sortBy.value = tsortBy
  sortDirection.value = tsortDirection
}

function handleRowHover(attributes: string[]) {
  if (!props.isHoverable) {
    return
  }

  hoveredAttributes.value = attributes
}

function handleRowLeave() {
  if (!props.isHoverable) {
    return
  }

  hoveredAttributes.value = []
}
</script>

<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.entries-table-container {
  overflow-x: auto;

  &--min-width {
    min-width: 400px;

    @include medium {
      min-width: 900px;
    }

    @include large {
      min-width: 1200px;
    }
  }
}
</style>
